/**
 * useTurnTimer — local countdown for the per-turn time limit.
 *
 * The protocol's `SessionSettings.turnTimer` is the per-turn duration in
 * seconds (0 = disabled). The server doesn't currently broadcast a
 * `turnStartedAt` field, so each client times locally: when it first
 * observes a new (turnNumber, currentPlayerIdx) pair it captures
 * `Date.now()` and counts down from there. All clients see the turn
 * change within a few hundred ms of each other (network jitter), so the
 * displayed countdowns stay in rough agreement without server help.
 *
 * Returns:
 *   - `enabled`: true iff `durationSec > 0`
 *   - `remainingSec`: integer seconds until expiry (0 when expired)
 *   - `isExpired`: convenience flag
 *
 * The hook re-renders ~4×/sec while enabled. When disabled it does not
 * schedule any timers and returns a stable zero state.
 */

import { useEffect, useRef, useState } from 'react';

export interface TurnTimerSnapshot {
  readonly enabled: boolean;
  readonly remainingSec: number;
  readonly isExpired: boolean;
}

interface UseTurnTimerArgs {
  readonly durationSec: number;
  readonly turnNumber: number;
  readonly currentPlayerIdx: number;
}

export function useTurnTimer({
  durationSec,
  turnNumber,
  currentPlayerIdx,
}: UseTurnTimerArgs): TurnTimerSnapshot {
  const enabled = durationSec > 0;

  // Wall-clock anchor for the current turn. Updated when turn changes.
  const startMsRef = useRef<number>(Date.now());

  // Tick state forces re-render. We don't store remainingSec in state —
  // we derive it on render from `Date.now() - startMsRef.current`.
  const [, setTick] = useState(0);

  useEffect(() => {
    startMsRef.current = Date.now();
    setTick((t) => t + 1);
  }, [turnNumber, currentPlayerIdx]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, [enabled]);

  if (!enabled) {
    return { enabled: false, remainingSec: 0, isExpired: false };
  }

  const elapsedMs = Date.now() - startMsRef.current;
  const remainingMs = Math.max(0, durationSec * 1000 - elapsedMs);
  const remainingSec = Math.ceil(remainingMs / 1000);
  return {
    enabled: true,
    remainingSec,
    isExpired: remainingMs <= 0,
  };
}

/** Format seconds → "M:SS" for display. */
export function formatTimerMMSS(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}
